import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Share2, Users, Gift, Ticket, Check, Trophy, Crown, Medal, Award, History, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PurchaseButton from "@/components/PurchaseButton";

const BADGE_ICON = { gold: Crown, silver: Medal, bronze: Award };
const BADGE_COLOR = { gold: "text-amber-500", silver: "text-slate-400", bronze: "text-orange-600" };

const REASON_LABEL = {
  referral: "Arkadaş daveti",
  referred_signup: "Davet ile kayıt bonusu",
  redeem: "Davet kodu kullanma",
  mock_exam: "Premium deneme sınavı",
  ai_report: "AI performans raporu",
  preference: "AI tercih danışmanlığı",
  monthly_reward: "Aylık ödül",
  admin_grant: "Yönetici ekledi",
};

export default function Referrals() {
  const [data, setData] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [board, setBoard] = useState(null);
  const [period, setPeriod] = useState("month");
  const [txs, setTxs] = useState([]);
  const [packages, setPackages] = useState([]);

  const load = async () => {
    const [r, b, t, p] = await Promise.all([
      api.get(`${V1}/users/me/referral`),
      api.get(`${V1}/referrals/leaderboard?period=${period}`),
      api.get(`${V1}/users/me/credit-transactions?limit=20`),
      api.get(`${V1}/credit-packages`),
    ]);
    setData(r.data);
    setBoard(b.data);
    setTxs(t.data?.transactions || []);
    setPackages(p.data || []);
  };

  useEffect(() => { load(); }, [period]);

  const link = data ? `${window.location.origin}${data.referral_link}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Bağlantı kopyalandı");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Kopyalanamadı"); }
  };

  const share = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: "SanoStudent — Bursluluk sınavları ve deneme sınavları",
        text: "Türkiye'nin bursluluk platformuna kayıt ol, birlikte haftalık deneme sınavı kredisi kazanalım!",
        url: link,
      });
    } catch {
      // Kullanıcı paylaşımı iptal etti — sessizce yok say
    }
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    try {
      const { data: res } = await api.post(`${V1}/users/me/redeem`, { code: redeemCode.trim() });
      toast.success(`+${res.credits_earned} kredi kazandın!`);
      setRedeemCode("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kod geçersiz"); }
  };

  if (!data || !board) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const BadgeIcon = data.badge ? BADGE_ICON[data.badge.code] : null;

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <div className="overline text-accent">Arkadaşını Davet Et</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Gift className="h-7 w-7 text-accent" /> Referans Ödülü</h1>
        <p className="text-muted-foreground mt-1">
          Davet bağlantını paylaş, her yeni kayıtta <b>sen</b> ve <b>davet ettiğin</b>{" "}
          <span className="text-accent font-bold">{data.credit_per_invite} kredi</span> kazan. Kredilerle premium deneme, AI rapor ve tercih danışmanlığı satın alabilirsin.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="p-6 rounded-xl border border-border bg-card">
          <Users className="h-6 w-6 text-primary mb-3" />
          <div className="text-3xl font-display font-bold" data-testid="stat-invited">{data.invited_count}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Toplam Davet</div>
        </div>
        <div className="p-6 rounded-xl border border-accent/30 bg-accent/5">
          <Ticket className="h-6 w-6 text-accent mb-3" />
          <div className="text-3xl font-display font-bold" data-testid="stat-credits">{data.exam_credits}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Kredi Bakiyen</div>
        </div>
        <div className="p-6 rounded-xl border border-border bg-card">
          <Gift className="h-6 w-6 text-primary mb-3" />
          <div className="text-3xl font-display font-bold">{data.credit_per_invite}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Her Davetin Değeri</div>
        </div>
        <div className="p-6 rounded-xl border border-border bg-card" data-testid="badge-card">
          {BadgeIcon ? <BadgeIcon className={`h-6 w-6 mb-3 ${BADGE_COLOR[data.badge.code]}`} /> : <Trophy className="h-6 w-6 text-muted-foreground mb-3" />}
          <div className="text-lg font-display font-bold">{data.badge?.label || "Henüz Rozet Yok"}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
            {board.me.next_badge ? `${board.me.next_badge.label}: ${board.me.next_badge.min - data.invited_count} davet daha` : "En yüksek rozet"}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-accent/30 bg-accent/5 p-6 space-y-4">
        <div className="font-display font-bold text-lg">Davet Bağlantın</div>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <Input value={link} readOnly className="font-mono text-sm" data-testid="referral-link-input" />
          <Button onClick={copy} variant="outline" data-testid="copy-link-btn">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Kopyalandı" : "Kopyala"}
          </Button>
          <Button onClick={share} data-testid="share-link-btn"><Share2 className="h-4 w-4" /> Paylaş</Button>
        </div>
        <div className="text-sm">
          Davet kodun: <span className="font-mono font-bold text-lg" data-testid="referral-code">{data.referral_code}</span>
        </div>
      </div>

      {/* Standalone credit packages — one-off top-up, priced above subscription value on purpose */}
      {packages.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="credit-packages-section">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-accent" /> Kredi Satın Al</div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Aboneliğe geçmeden tek seferlik kredi almak istersen paketlerden birini seç. Not: kredi başı fiyat,
            abonelik planlarına göre daha yüksektir — düzenli kullanıyorsan abonelik daha avantajlıdır.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.code}
                className={`p-5 rounded-xl border flex flex-col gap-3 ${pkg.highlight ? "border-accent bg-accent/5 shadow-md" : "border-border bg-background"}`}
                data-testid={`credit-package-${pkg.code}`}
              >
                {pkg.highlight && (
                  <span className="self-start text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-bold">En Popüler</span>
                )}
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-accent" />
                  <div className="font-display font-bold text-2xl">{pkg.credits} Kredi</div>
                </div>
                <div className="text-3xl font-display font-bold">₺{pkg.price}</div>
                <div className="text-xs text-muted-foreground">Kredi başı ₺{(pkg.price / pkg.credits).toFixed(0)}</div>
                <PurchaseButton
                  kind="credit_package"
                  code={pkg.code}
                  cycle="monthly"
                  price={pkg.price}
                  label={`₺${pkg.price} · Satın Al`}
                  variant={pkg.highlight ? "default" : "outline"}
                  testId={`buy-credit-package-${pkg.code}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="referral-leaderboard">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="font-display font-bold text-lg flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Liderlik Tablosu</div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="month" data-testid="lb-tab-month">Bu Ay</TabsTrigger>
              <TabsTrigger value="all" data-testid="lb-tab-all">Tüm Zamanlar</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {period === "month" && (
          <div className="text-xs text-muted-foreground mb-3">
            Ay sonunda ilk 3 sıra: <b>{board.rewards.join(" / ")}</b> kredi bonus kazanır.
          </div>
        )}
        {board.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Henüz sıralama yok. İlk davet edenlerden biri sen ol!</div>
        ) : (
          <div className="space-y-2">
            {board.rows.map((row) => {
              const RBadge = row.badge ? BADGE_ICON[row.badge.code] : null;
              const isMe = row.user_id === board.me.user_id;
              return (
                <div key={row.user_id} className={`flex items-center gap-3 p-3 rounded ${isMe ? "bg-accent/10 border border-accent/30" : "bg-muted/30"}`} data-testid={`lb-row-${row.rank}`}>
                  <div className="w-8 text-center font-display font-bold">
                    {row.rank <= 3 ? ["🥇","🥈","🥉"][row.rank - 1] : `#${row.rank}`}
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <span className="font-medium">{row.name}</span>
                    {RBadge && <RBadge className={`h-4 w-4 ${BADGE_COLOR[row.badge.code]}`} />}
                    {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground uppercase tracking-widest">Sen</span>}
                  </div>
                  <div className="text-sm font-mono"><b>{row.invited_count}</b> davet</div>
                  {row.reward_credits > 0 && (
                    <div className="text-xs text-accent font-bold">+{row.reward_credits} kredi</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {board.me.rank == null && board.me.invited_count > 0 && (
          <div className="mt-3 text-xs text-muted-foreground">
            {period === "month" ? "Bu ay" : "Toplam"} <b>{board.me.invited_count}</b> davetin var — tabloya girmen için birkaç arkadaşını daha davet et.
          </div>
        )}
      </div>

      {/* Redeem code */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="font-display font-bold text-lg mb-2">Bir Kodum Var</div>
        <p className="text-sm text-muted-foreground mb-3">Arkadaşından davet kodu aldıysan aşağıya girip {data.credit_per_invite} kredi kazan.</p>
        <div className="flex gap-2">
          <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="XXXXXXXX" className="font-mono" data-testid="redeem-input" />
          <Button onClick={redeem} data-testid="redeem-btn">Kodu Kullan</Button>
        </div>
      </div>

      {/* Credit history */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="credit-history">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2"><History className="h-5 w-5" /> Kredi Hareketleri</div>
        {txs.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 text-center">Henüz kredi hareketin yok.</div>
        ) : (
          <div className="divide-y divide-border">
            {txs.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`tx-row-${t.id}`}>
                <div>
                  <div className="font-medium">{REASON_LABEL[t.reason] || t.reason}</div>
                  <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("tr-TR")}</div>
                </div>
                <div className={`font-mono font-bold ${t.delta > 0 ? "text-success" : "text-destructive"}`}>
                  {t.delta > 0 ? `+${t.delta}` : t.delta}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
